// src/modules/stock/Scanner/Scanner.js
import React, { useState, useEffect, useRef } from "react";
import {
    Card, Row, Col, Tabs, Tab, Button, Spinner
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Footer from "../../../layouts/Footer";
import Header from "../../../layouts/Header";
import ScannerCamera from "./ScannerCamera";
import ScannerManuel from "./ScannerManuel";
import ResultatScan from "./ResultatScan";
import HistoriqueScans from "./HistoriqueScans";
import { formatCurrency } from "../../../utils/formatters";
import { API_BASE_URL } from "../../../constants/api";
import { COLORS } from "../../../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function Scanner() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("camera");
    const [scanResult, setScanResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [mode, setMode] = useState('recherche');
    const [quantite, setQuantite] = useState(1);
    const [commentaire, setCommentaire] = useState('');
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });
    const [alertes, setAlertes] = useState([]);

    const [historiqueKey, setHistoriqueKey] = useState(Date.now());
    const historiqueRef = useRef(null);

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    const getToken = () => localStorage.getItem("token");

    useEffect(() => {
        fetchStats();
    }, []);

    /* ============================================================
       FETCH STATS
       ============================================================ */
    const fetchStats = async () => {
        try {
            const token = getToken();
            const response = await fetch(
                `${API_BASE_URL}/scanner.php?action=stats`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error(`Erreur HTTP: ${response.status}`);
            const result = await response.json();
            if (result.success) setStats(result.data);
        } catch (err) {
            console.error("Erreur chargement stats", err);
        }
    };

    /* ============================================================
       ACTIONS
       ============================================================ */
    const handleScan = async (code) => {
        setLoading(true);
        setError(null);
        setAlertes([]);

        try {
            const token = getToken();
            const response = await fetch(
                `${API_BASE_URL}/scanner.php?action=rechercher&code=${encodeURIComponent(code)}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error(`Erreur HTTP: ${response.status}`);

            const result = await response.json();

            if (result.success) {
                setScanResult(result.data);

                const alertesList = [];
                if (result.data.est_en_alerte) {
                    alertesList.push({
                        type: 'danger',
                        message: `Stock critique : ${result.data.stock_actuel} unités (seuil: ${result.data.seuil_alerte})`
                    });
                }
                if (result.data.stock_actuel === 0) {
                    alertesList.push({
                        type: 'warning',
                        message: 'Produit en rupture de stock'
                    });
                }
                setAlertes(alertesList);
            } else {
                setError(result.message || "Produit non trouvé");
                setScanResult(null);
                showToast('error', 'Produit non trouvé', result.message || `Aucun produit avec le code "${code}"`);
            }
        } catch (err) {
            setError(err.message || "Erreur lors du scan");
            showToast('error', 'Erreur', err.message || "Erreur lors du scan");
            console.error("Erreur scan:", err);
        } finally {
            setLoading(false);
        }
    };

    const refreshHistorique = () => {
        setHistoriqueKey(Date.now());
        if (historiqueRef.current && historiqueRef.current.refresh) {
            historiqueRef.current.refresh();
        }
    };

    const handleEntreeStock = async () => {
        if (!scanResult || quantite <= 0) return;

        setLoading(true);
        try {
            const token = getToken();
            const userStr = localStorage.getItem("user");
            const user = JSON.parse(userStr);

            const response = await fetch(
                `${API_BASE_URL}/scanner.php`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'entree',
                        code_barre: scanResult.code_barre || scanResult.reference,
                        quantite: parseInt(quantite),
                        commentaire: commentaire,
                        id_utilisateur: user.id_utilisateur || user.id
                    })
                }
            );

            if (!response.ok) throw new Error(`Erreur HTTP: ${response.status}`);

            const result = await response.json();

            if (result.success) {
                showToast('success', 'Entrée enregistrée',
                    `+${result.data.quantite_ajoutee} ${scanResult.designation} ajouté(s) au stock`
                );
                await handleScan(scanResult.code_barre || scanResult.reference);
                setQuantite(1);
                setCommentaire('');
                await fetchStats();
                refreshHistorique();
            } else {
                setError(result.message || "Erreur lors de l'entrée");
                showToast('error', 'Erreur', result.message || "Erreur lors de l'entrée");
            }
        } catch (err) {
            setError(err.message || "Erreur lors de l'entrée");
            showToast('error', 'Erreur', err.message || "Erreur lors de l'entrée");
            console.error("Erreur entrée stock:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSortieStock = async () => {
        if (!scanResult || quantite <= 0) return;

        setLoading(true);
        try {
            const token = getToken();
            const userStr = localStorage.getItem("user");
            const user = JSON.parse(userStr);

            const response = await fetch(
                `${API_BASE_URL}/scanner.php`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'sortie',
                        code_barre: scanResult.code_barre || scanResult.reference,
                        quantite: parseInt(quantite),
                        commentaire: commentaire,
                        id_utilisateur: user.id_utilisateur || user.id
                    })
                }
            );

            if (!response.ok) throw new Error(`Erreur HTTP: ${response.status}`);

            const result = await response.json();

            if (result.success) {
                showToast('success', 'Sortie enregistrée',
                    `−${result.data.quantite_retiree} ${scanResult.designation} retiré(s) du stock`
                );
                await handleScan(scanResult.code_barre || scanResult.reference);
                setQuantite(1);
                setCommentaire('');
                await fetchStats();
                refreshHistorique();

                if (result.data.alerte) {
                    setAlertes([{ type: 'warning', message: result.data.alerte }]);
                }
            } else {
                setError(result.message || "Erreur lors de la sortie");
                showToast('error', 'Erreur', result.message || "Erreur lors de la sortie");
            }
        } catch (err) {
            setError(err.message || "Erreur lors de la sortie");
            showToast('error', 'Erreur', err.message || "Erreur lors de la sortie");
            console.error("Erreur sortie stock:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleVoirProduit = () => {
        if (scanResult) navigate(`/stock/produits/${scanResult.id_produit}`);
    };

    const handleModeChange = (newMode) => {
        setMode(newMode);
        setQuantite(1);
        setCommentaire('');
        setError(null);
    };

    /* ============================================================
       HELPERS
       ============================================================ */
    const getStockStatusColor = (stock, seuil) => {
        if (stock <= 0) return COLORS.danger;
        if (stock <= seuil) return COLORS.warning;
        return COLORS.success;
    };

    const getStockProgress = (stock, max = 100) => {
        return Math.min((stock / max) * 100, 100);
    };

    /* ============================================================
       LOADING — Bootstrap spinner (conservé)
       ============================================================ */
    if (loading && !scanResult) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <Spinner
                                animation="border"
                                role="status"
                                className="mb-3"
                                style={{ width: '3rem', height: '3rem', color: COLORS.primary }}
                            >
                                <span className="visually-hidden">Chargement...</span>
                            </Spinner>
                            <h5 className="text-muted">Initialisation du scanner...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-qr-code-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Scanner codes-barres</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-information-line"></i>
                                        Rechercher, ajouter ou retirer du stock
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button
                                className={mode === 'recherche' ? 'btn-modern-primary' : 'btn-modern-outline'}
                                onClick={() => handleModeChange('recherche')}
                            >
                                <i className="ri-search-line"></i> Recherche
                            </Button>
                            <Button
                                className={mode === 'entree' ? 'btn-modern-success' : 'btn-modern-outline'}
                                onClick={() => handleModeChange('entree')}
                            >
                                <i className="ri-add-circle-line"></i> Entrée
                            </Button>
                            <Button
                                className={mode === 'sortie' ? 'btn-modern-danger' : 'btn-modern-outline'}
                                onClick={() => handleModeChange('sortie')}
                            >
                                <i className="ri-subtract-line"></i> Sortie
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm="6" xl="6">
                                <div className="kpi-charte kpi-primary">
                                    <div className="kpi-icon-charte"><i className="ri-qr-code-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Scans aujourd'hui</span>
                                        <h3 className="kpi-value-charte">{stats.aujourdhui || 0}</h3>
                                        <span className="kpi-unit-charte">scans effectués</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="6">
                                <div className="kpi-charte kpi-success">
                                    <div className="kpi-icon-charte"><i className="ri-trophy-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Top scan</span>
                                        <h3 className="kpi-value-charte" style={{ fontSize: '1rem' }}>
                                            {stats.top_produits?.[0]?.designation || '—'}
                                        </h3>
                                        <span className="kpi-unit-charte">produit le plus scanné</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    <Row className="g-4">
                        <Col lg={activeTab === 'historique' ? 12 : 7}>
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="card-header-gradient p-0">
                                    <Tabs
                                        activeKey={activeTab}
                                        onSelect={(k) => setActiveTab(k)}
                                        className="scanner-tabs-charte"
                                    >
                                        <Tab
                                            eventKey="camera"
                                            title={
                                                <span>
                                                    <i className="ri-camera-line me-2"></i>
                                                    Caméra
                                                </span>
                                            }
                                        />
                                        <Tab
                                            eventKey="manuel"
                                            title={
                                                <span>
                                                    <i className="ri-keyboard-line me-2"></i>
                                                    Saisie manuelle
                                                </span>
                                            }
                                        />
                                        <Tab
                                            eventKey="historique"
                                            title={
                                                <span>
                                                    <i className="ri-history-line me-2"></i>
                                                    Historique
                                                </span>
                                            }
                                        />
                                    </Tabs>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    {activeTab === 'camera' && (
                                        <ScannerCamera
                                            onScan={handleScan}
                                            loading={loading}
                                            mode={mode}
                                        />
                                    )}
                                    {activeTab === 'manuel' && (
                                        <ScannerManuel
                                            onScan={handleScan}
                                            loading={loading}
                                            mode={mode}
                                        />
                                    )}
                                    {activeTab === 'historique' && (
                                        <HistoriqueScans
                                            key={historiqueKey}
                                            ref={historiqueRef}
                                        />
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        {activeTab !== 'historique' && (
                            <Col lg={5}>
                                <ResultatScan
                                    scanResult={scanResult}
                                    loading={loading}
                                    error={error}
                                    alertes={alertes}
                                    mode={mode}
                                    quantite={quantite}
                                    setQuantite={setQuantite}
                                    commentaire={commentaire}
                                    setCommentaire={setCommentaire}
                                    onEntree={handleEntreeStock}
                                    onSortie={handleSortieStock}
                                    onVoirProduit={handleVoirProduit}
                                    getStockStatusColor={getStockStatusColor}
                                    getStockProgress={getStockProgress}
                                    formatCurrency={formatCurrency}
                                />
                            </Col>
                        )}
                    </Row>
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 0 !important;
                    border: none;
                }

                /* ===== SCANNER TABS ===== */
                .scanner-tabs-charte {
                    border-bottom: none !important;
                    padding: 0 1rem;
                }
                .scanner-tabs-charte .nav-item {
                    margin-bottom: 0;
                }
                .scanner-tabs-charte .nav-link {
                    border: none;
                    color: rgba(255,255,255,0.7);
                    font-weight: 500;
                    padding: 1rem 1.25rem;
                    transition: all 0.2s ease;
                    background: transparent;
                    border-radius: 0;
                    border-bottom: 3px solid transparent;
                }
                .scanner-tabs-charte .nav-link:hover {
                    color: white;
                    background: rgba(255,255,255,0.08);
                }
                .scanner-tabs-charte .nav-link.active {
                    color: white !important;
                    background: rgba(255,255,255,0.12);
                    border-bottom: 3px solid white;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-success {
                    background: linear-gradient(135deg, var(--success), #059669);
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(16,185,129,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-success:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(16,185,129,0.4); color: white;
                }
                .btn-modern-danger {
                    background: linear-gradient(135deg, var(--danger), #dc2626);
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(239,68,68,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-danger:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(239,68,68,0.4); color: white;
                }
                .btn-modern-outline:disabled,
                .btn-modern-primary:disabled,
                .btn-modern-success:disabled,
                .btn-modern-danger:disabled { opacity: 0.6; transform: none; }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                    .scanner-tabs-charte .nav-link {
                        padding: 0.75rem 0.75rem;
                        font-size: 0.8rem;
                    }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}